import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import SectionLabel from '@/components/SectionLabel';
import BlogCard from '@/components/BlogCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'All Categories',
  'Research',
  'Profiles',
  'Governance',
  'Events',
  'Podcast',
  'From the Founder',
  'Data & Evidence',
];

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get('category') || 'All Categories'
  );

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/posts`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
        // Use fallback data
        setPosts(FALLBACK_POSTS);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Update URL when category changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory !== 'All Categories') {
      params.set('category', selectedCategory);
    }
    setSearchParams(params);
  }, [selectedCategory, setSearchParams]);

  // Filtered posts
  const filteredPosts = useMemo(() => {
    if (selectedCategory === 'All Categories') {
      return posts;
    }
    return posts.filter((post) => 
      post.category?.includes(selectedCategory)
    );
  }, [posts, selectedCategory]);

  return (
    <div data-testid="blog-page" className="min-h-screen">
      <SEO 
        title="Research & Insights on Women in Leadership"
        description="Evidence-based research, data analysis, and insights on women in leadership, gender equity, corporate governance, and women entrepreneurs. Forbes and Harvard-level intellectual content."
        keywords={['women leadership research', 'gender gap studies', 'women in business articles', 'female leadership insights', 'corporate diversity research', 'women CEO studies']}
        url="https://shebuiltthis.com/blog"
        image="https://images.unsplash.com/photo-1758518727592-706e80ebc354?w=1200&q=80"
      />
      {/* Hero */}
      <section className="bg-sbt-black pt-32 pb-16 px-6">
        <div className="max-w-[1400px] mx-auto text-center">
          <SectionLabel className="mb-4">From the Journal</SectionLabel>
          <h1 className="font-display text-4xl lg:text-[64px] font-bold text-sbt-white mb-6">
            Insight. Research. Truth.
          </h1>
          <p className="font-body text-lg text-sbt-dim max-w-2xl mx-auto">
            Stories, research, and analysis from She Built This — 
            documenting what women build and why it matters.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-20 z-40 bg-[#FAF9F6] border-b border-[rgba(201,168,76,0.12)]">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex flex-wrap gap-3">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 font-mono text-xs tracking-wide transition-all ${
                  selectedCategory === category
                    ? 'bg-[#C25E3E] text-[#080808]'
                    : 'bg-transparent text-[rgba(250,248,244,0.5)] border border-[rgba(250,248,244,0.1)] hover:border-[#C25E3E] hover:text-[#C25E3E]'
                }`}
                data-testid={`filter-${category.toLowerCase().replace(/ /g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="bg-[#FAF9F6] section-padding">
        <div className="max-w-[1400px] mx-auto px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.04)] p-6 animate-pulse">
                  <div className="h-6 bg-[rgba(255,255,255,0.05)] w-20 mb-4" />
                  <div className="h-8 bg-[rgba(255,255,255,0.05)] w-full mb-3" />
                  <div className="h-4 bg-[rgba(255,255,255,0.05)] w-32 mb-3" />
                  <div className="h-16 bg-[rgba(255,255,255,0.05)]" />
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.4)' }}>
                No posts found in this category.
              </p>
              <button
                onClick={() => setSelectedCategory('All Categories')}
                className="mt-6 inline-flex items-center h-12 px-8 border border-[#C25E3E] text-[#C25E3E] text-[12px] tracking-[0.1em] uppercase transition-all hover:bg-[#C25E3E] hover:text-[#080808]"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                View All Posts
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// Fallback posts data - All 25 Forbes/Harvard-level articles
const FALLBACK_POSTS = [
  // Research & Data
  {
    slug: 'zero-the-number-that-changes-everything',
    title: 'Zero: The Number That Changes Everything',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'After fifty years of research, zero programs have been proven to increase women in leadership over time. Here is what the evidence actually says.',
    image: 'https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=800&q=80',
  },
  {
    slug: 'the-sponsorship-gap',
    title: 'The Sponsorship Gap: Why Mentorship Alone Will Never Close the Gender Gap',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'New research reveals sponsorship — not mentorship — is the missing piece. Men are 46% more likely to have a sponsor than women.',
    image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&q=80',
  },
  {
    slug: '676-million-women',
    title: '676 Million Women Live Next to a War. Here Is What They Built Anyway.',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'The UN reported 676 million women living in conflict-affected areas. 87% increase in sexual violence. Yet women continue to build.',
    image: 'https://images.pexels.com/photos/5915200/pexels-photo-5915200.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    slug: 'the-broken-rung',
    title: 'The Broken Rung: Where Women\'s Careers Actually Stall',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'New McKinsey data reveals the first promotion is the real barrier. For every 100 men promoted to manager, only 87 women are promoted.',
    image: 'https://images.unsplash.com/photo-1573166364524-d9dbfd8bbf83?w=800&q=80',
  },
  {
    slug: 'why-women-leave-tech',
    title: 'Why 56% of Women Leave Tech by Mid-Career',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'It\'s not work-life balance — it\'s the culture, the credit, and the code reviews. Women\'s code is accepted more when gender is hidden.',
    image: 'https://images.unsplash.com/photo-1758518727592-706e80ebc354?w=800&q=80',
  },
  {
    slug: 'the-confidence-myth',
    title: 'The Confidence Myth: Why Telling Women to "Speak Up" Misses the Point',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Research reveals the problem isn\'t women\'s confidence — it\'s how their confidence is perceived. Women are interrupted 2.1x more.',
    image: 'https://images.unsplash.com/photo-1573166675921-076ea6b621ce?w=800&q=80',
  },
  {
    slug: 'middle-east-women-leadership',
    title: 'Why the Middle East Is the Most Important Region for Women\'s Leadership Right Now',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'UAE ranked #1 in MENA and #13 globally for gender equality. 70% of university graduates are women. Here\'s what\'s driving the acceleration.',
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80',
  },
  {
    slug: 'venture-capital-gender-gap',
    title: 'The 2.1% Problem: Why Women Founders Can\'t Access Capital',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Women founders generate 78 cents per dollar invested vs. 31 cents for men. Yet they receive only 2.1% of VC funding.',
    image: 'https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?w=800&q=80',
  },
  {
    slug: 'board-diversity-returns',
    title: 'Does Board Diversity Actually Improve Returns? Here\'s What the Research Shows.',
    category: 'Research | Governance',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Unpacking the evidence on gender diversity and corporate performance. Top quartile diversity shows 25% higher profitability.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
  },
  {
    slug: 'return-to-office-gender',
    title: 'The Return-to-Office Debate Has a Gender Problem No One Is Talking About',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'RTO mandates disproportionately impact women\'s career advancement. 64% of caregivers are women. 29% considering leaving due to RTO.',
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80',
  },
  {
    slug: 'pay-gap-lifetime-cost',
    title: 'The $1.2 Million Gap: What the Gender Pay Gap Actually Costs Over a Career',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'The pay gap isn\'t 18 cents. It\'s $1.2 million. And it compounds through retirement savings, investment returns, and the wealth gap.',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80',
  },
  {
    slug: 'mentorship-vs-sponsorship',
    title: 'Mentorship vs. Sponsorship: Why One Advances Careers and the Other Doesn\'t',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Mentors talk to you. Sponsors talk about you. The difference is the difference between advice and advancement.',
    image: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=800&q=80',
  },
  {
    slug: 'quota-debate',
    title: 'The Quota Debate: What 20 Years of Data from Norway Actually Shows',
    category: 'Research | Governance',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Norway mandated 40% women on boards in 2006. Two decades later, the evidence is in. Here\'s what happened.',
    image: 'https://images.unsplash.com/photo-1573497701240-345a300b8d36?w=800&q=80',
  },
  {
    slug: 'imposter-syndrome-reframe',
    title: 'Imposter Syndrome Isn\'t Your Problem. It\'s the System\'s Symptom.',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'We\'ve been diagnosing women when we should be diagnosing environments. Imposter syndrome is a rational response to biased systems.',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80',
  },
  {
    slug: 'negotiation-for-women',
    title: 'The Negotiation Paradox: Why the Advice Women Get Makes Things Worse',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Women who negotiate are 30% less likely to be recommended for hiring. The advice to negotiate like men ignores the penalty.',
    image: 'https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?w=800&q=80',
  },
  // New articles
  {
    slug: 'ai-and-women-leadership',
    title: 'The AI Revolution Will Either Accelerate or Demolish Women\'s Progress',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Women are 22% of the AI workforce. 60% of roles at risk are held by women. AI could add $400B to women\'s economic output — if they adopt it.',
    image: 'https://images.unsplash.com/photo-1677442135136-760c813028c0?w=800&q=80',
  },
  {
    slug: 'healthcare-leadership-gap',
    title: 'Women Are 76% of Healthcare Workers. Why Are They Only 16% of Hospital CEOs?',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Healthcare is the most female-dominated major industry. Yet it is led almost entirely by men. This is not a pipeline problem.',
    image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80',
  },
  {
    slug: 'the-wealth-building-gap',
    title: 'Why Women Own 32 Cents for Every Dollar Men Own',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'The gender wealth gap is three times larger than the pay gap. The difference is how money compounds — or doesn\'t — over a lifetime.',
    image: 'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=800&q=80',
  },
  {
    slug: 'remote-work-opportunity',
    title: 'Remote Work Was the Biggest Gender Equity Win in Decades. Now It\'s Being Reversed.',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Flexibility is infrastructure for equality. Companies dismantling it are dismantling decades of progress.',
    image: 'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800&q=80',
  },
  {
    slug: 'first-generation-professionals',
    title: 'The First-Generation Professional Penalty: What No One Talks About',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'The rules of professional advancement are rarely written down. They are passed from generation to generation — unless yours never had them.',
    image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80',
  },
  {
    slug: 'climate-and-women-leadership',
    title: 'Climate Change Will Affect Women Disproportionately. Women Leaders Are Building Solutions Differently.',
    category: 'Research | Data & Evidence',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: '80% of climate refugees are women. Women-led climate initiatives show 25% better outcomes — with 2% of funding.',
    image: 'https://images.unsplash.com/photo-1569163139599-0f4517e36f51?w=800&q=80',
  },
  // From the Founder & Profiles
  {
    slug: 'profile-001-dr-neha-dewan',
    title: 'Profile #001: Why Dr. Neha Dewan is the first name on She Built This',
    category: 'Profiles | From the Founder',
    author: 'She Built This Editorial',
    date: 'March 2026',
    excerpt: 'Every platform has a founding story. She Built This begins with its founder.',
    image: 'https://customer-assets.emergentagent.com/job_22d79c15-8eba-437a-975f-6355374d527d/artifacts/yeqwbvor_image.png',
  },
  {
    slug: '595-profiles-what-we-learned',
    title: '595 Profiles Later: What We Learned About Women Who Build',
    category: 'Research | From the Founder',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'Patterns from the She Built This database — what women who reached the top actually have in common.',
    image: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=800&q=80',
  },
  {
    slug: 'letter-from-founder',
    title: 'A Letter from the Founder — Why She Built This Exists',
    category: 'From the Founder',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'I built She Built This because the stories I kept reading deserved better than a footnote.',
    image: 'https://images.pexels.com/photos/4755024/pexels-photo-4755024.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    slug: 'the-nine-frameworks',
    title: 'The Nine Frameworks: A Complete Guide to Systemic Barriers Women Face',
    category: 'Research',
    author: 'Dr. Neha Dewan',
    date: 'March 2026',
    excerpt: 'The research behind She Built This identifies nine distinct mechanisms that create obstacles for women in professional settings.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
  },
];

export default BlogPage;
